'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { motion } from 'framer-motion';
import { Award, Users, Heart } from 'lucide-react';

export default function AboutPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <Navbar data-zylo-id="zylo-d027a3d79894" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="34:7-34:17" data-zylo-component="AboutPage" />

      {/* Hero */}
      <section data-zylo-id="zylo-c52b79039158" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="37:7-57:17" data-zylo-component="AboutPage" className="bg-gradient-to-b from-primary/10 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-285450a68574" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="38:9-56:15" data-zylo-component="AboutPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-d7570f61d0a7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="39:11-55:24" data-zylo-component="AboutPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-3xl text-center"
          >
            <motion.h1 data-zylo-id="zylo-c8f5a0e702fd" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="45:13-50:25" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-4xl font-bold sm:text-5xl text-foreground mb-6"
            >
              About Blue Kraken Enterprise
            </motion.h1>
            <motion.p data-zylo-id="zylo-1b31b1026936" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="51:13-54:173" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >Two premium services, one commitment to excellence. Since 2025, we've been transforming vehicles and protecting homes with professionalism and care.</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section data-zylo-id="zylo-0fea5db17100" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="60:7-87:17" data-zylo-component="AboutPage" className="py-16 sm:py-24">
        <div data-zylo-id="zylo-0e6f4c5c1990" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="61:9-86:15" data-zylo-component="AboutPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-9cc9911a2586" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="62:11-85:24" data-zylo-component="AboutPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <motion.h2 data-zylo-id="zylo-d057ad92b3e7" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="69:13-74:25" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-3xl font-bold text-foreground"
            >
              Our Story
            </motion.h2>
            <motion.p data-zylo-id="zylo-3d565713bd4e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="75:13-78:319" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground leading-relaxed"
            >Blue Kraken Enterprise was founded with a simple vision: deliver exceptional service with integrity and professionalism. What started as a two-person auto detailing operation has grown into a comprehensive service provider offering both premium auto detailing and professional home inspections.</motion.p>
            <motion.p data-zylo-id="zylo-f845f7866953" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="79:13-84:24" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground leading-relaxed"
            >
              We believe that whether you're protecting your vehicle or your home, you deserve service from experienced professionals who care about quality and attention to detail. Every team member at Blue Kraken is trained, certified, and dedicated to exceeding expectations.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section data-zylo-id="zylo-82181da36fc9" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="90:7-140:17" data-zylo-component="AboutPage" className="bg-primary/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-f8acaeaf9649" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="91:9-139:15" data-zylo-component="AboutPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-99186c4035f1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="92:11-138:24" data-zylo-component="AboutPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl"
          >
            <motion.h2 data-zylo-id="zylo-eaadffcfd5af" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="99:13-104:25" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-3xl font-bold text-foreground mb-12 text-center"
            >
              Our Values
            </motion.h2>

            <div data-zylo-id="zylo-479dadfecb4a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="106:13-137:19" data-zylo-component="AboutPage" className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Award,
                  title: 'Excellence',
                  description: 'We pursue excellence in every detail of our work, from the smallest touch to the final result.'
                },
                {
                  icon: Users,
                  title: 'Integrity',
                  description: 'We conduct our business with honesty and transparency, earning trust through consistent actions.'
                },
                {
                  icon: Heart,
                  title: 'Care',
                  description: 'We genuinely care about our clients and their satisfaction is our primary measure of success.'
                }
              ].map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div data-zylo-id="zylo-9a7220737535" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="126:19-134:32" data-zylo-component="AboutPage" key={index} variants={itemVariants}>
                    <Card data-zylo-id="zylo-ee25dcf8ec17" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="127:21-133:28" data-zylo-component="AboutPage" className="border-0 shadow-lg h-full">
                      <CardContent data-zylo-id="zylo-1baacc4cf53d" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="128:23-132:37" data-zylo-component="AboutPage" className="p-6 text-center">
                        <Icon data-zylo-id="zylo-23096e29e55e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="129:25-129:81" data-zylo-component="AboutPage" className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 data-zylo-id="zylo-663044acbf9a" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="130:25-130:82" data-zylo-component="AboutPage" className="text-xl font-bold mb-2">{value.title}</h3>
                        <p data-zylo-id="zylo-0361b0aa5523" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="131:25-131:85" data-zylo-component="AboutPage" className="text-muted-foreground">{value.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section data-zylo-id="zylo-444584812712" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="143:7-170:17" data-zylo-component="AboutPage" className="bg-gradient-to-r from-primary to-primary/80 py-16 sm:py-24 text-white">
        <div data-zylo-id="zylo-4b59e1cadbe2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="144:9-169:15" data-zylo-component="AboutPage" className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div data-zylo-id="zylo-eb8fd8d12ac5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="145:11-168:24" data-zylo-component="AboutPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 data-zylo-id="zylo-97f11a61db1a" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="151:13-156:25" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl mb-4"
            >
              Ready to Experience the Blue Kraken Difference?
            </motion.h2>
            <motion.div data-zylo-id="zylo-c261b516ed45" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="157:13-167:26" data-zylo-component="AboutPage"
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-8"
            >
              <Button data-zylo-id="zylo-408358082298" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="161:15-163:24" data-zylo-component="AboutPage" size="lg" asChild className="bg-white text-primary hover:bg-gray-100">
                <Link data-zylo-id="zylo-ca53426e0f5b" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="162:17-162:67" data-zylo-component="AboutPage" href="/detailing">Book Auto Detailing</Link>
              </Button>
              <Button data-zylo-id="zylo-1120879d402e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="164:15-166:24" data-zylo-component="AboutPage" size="lg" variant="outline" asChild className="border-white text-white hover:bg-white/10">
                <Link data-zylo-id="zylo-c569d45c6fd5" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="165:17-165:70" data-zylo-component="AboutPage" href="/inspections">Book Home Inspection</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-c17cb5f16cfa" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/about/page.tsx" data-zylo-loc="172:7-172:17" data-zylo-component="AboutPage" />
    </>
  );
}
